import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { useD320Query } from './hooks/useD320Query';
import type { ReportRow } from '../../services/d320Api';
import './D320Form.css';

const D320Form: React.FC = () => {
  const { loading, data, groupCounts, queried, noData, runQuery, reset } = useD320Query();

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [productFrom, setProductFrom] = useState('');
  const [productTo, setProductTo] = useState('');
  const [itemSpecFrom, setItemSpecFrom] = useState('');
  const [itemSpecTo, setItemSpecTo] = useState('');
  const [processFrom, setProcessFrom] = useState('');
  const [processTo, setProcessTo] = useState('');

  const handleQuery = () => {
    runQuery({
      date_from: dateFrom.trim() || undefined,
      date_to: dateTo.trim() || undefined,
      product_from: productFrom.trim() || undefined,
      product_to: productTo.trim() || undefined,
      item_spec_from: itemSpecFrom.trim() || undefined,
      item_spec_to: itemSpecTo.trim() || undefined,
      process_from: processFrom.trim() || undefined,
      process_to: processTo.trim() || undefined,
    });
  };

  const handleReset = () => {
    setDateFrom('');
    setDateTo('');
    setProductFrom('');
    setProductTo('');
    setItemSpecFrom('');
    setItemSpecTo('');
    setProcessFrom('');
    setProcessTo('');
    reset();
  };

  const handlePrint = () => {
    if (!data || data.length === 0) return;
    const title = '廠商工繳比較表';
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>${title}</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:11px}h2{text-align:center}table{width:100%;border-collapse:collapse}th,td{border:1px solid #333;padding:2px 5px}th{background:#f0f0f0;text-align:center}.right{text-align:right}@media print{body{margin:10mm}}</style></head><body>
<h2>${title}</h2>
<table><thead><tr><th>廠商代號</th><th>廠商名稱</th><th>品項組合</th><th>產品代號</th><th>工繳單價</th></tr></thead><tbody>
${data.map(r => `<tr><td>${r.FD130_15}</td><td>${r.vendor_name}</td><td>${r.item_combo}</td><td>${r.FD130_12}</td><td class="right">${r.unit_price?.toFixed(3)}</td></tr>`).join('')}
</tbody></table>
<p style="margin-top:8px">共 ${data.length} 筆</p>
</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const columns: ColumnsType<ReportRow> = [
    { title: '廠商代號', dataIndex: 'FD130_15', key: 'FD130_15', width: 100 },
    { title: '廠商名稱', dataIndex: 'vendor_name', key: 'vendor_name', ellipsis: true, width: 150 },
    { title: '品項組合', dataIndex: 'item_combo', key: 'item_combo', ellipsis: true, width: 200 },
    { title: '產品代號', dataIndex: 'FD130_12', key: 'FD130_12', width: 120 },
    { title: '工繳單價', dataIndex: 'unit_price', key: 'unit_price', width: 110, align: 'right' as const, render: (v: number) => v?.toFixed(3) },
  ];

  const filterLabel = (text: string) => (
    <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>{text}</label>
  );

  return (
    <PermissionGuard module={Modules.D320} action="query">
      <div className="d320-container">
        <div className="d320-header">
          <h2>廠商工繳比較表</h2>
          {queried && !noData && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>
          )}
        </div>
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space wrap size="middle">
            <div>
              {filterLabel('託工日期起 (YYYY-MM-DD)')}
              <Input value={dateFrom} onChange={e => setDateFrom(e.target.value)} placeholder="例 2026-01-01" style={{ width: 140 }} allowClear />
            </div>
            <div>
              {filterLabel('託工日期迄 (YYYY-MM-DD)')}
              <Input value={dateTo} onChange={e => setDateTo(e.target.value)} placeholder="例 2026-12-31" style={{ width: 140 }} allowClear />
            </div>
            <div>
              {filterLabel('產品代號起')}
              <Input value={productFrom} onChange={e => setProductFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('產品代號迄')}
              <Input value={productTo} onChange={e => setProductTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('品項規格起')}
              <Input value={itemSpecFrom} onChange={e => setItemSpecFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('品項規格迄')}
              <Input value={itemSpecTo} onChange={e => setItemSpecTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('工繳代號起')}
              <Input value={processFrom} onChange={e => setProcessFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('工繳代號迄')}
              <Input value={processTo} onChange={e => setProcessTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>查詢</Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
              </Space>
            </div>
          </Space>
        </Card>
        {queried && noData && <Empty description="查無此資料" style={{ marginTop: 40 }} />}
        {queried && !noData && (
          <>
            {groupCounts.length > 0 && (
              <div style={{ marginBottom: 12, fontSize: 13, color: '#555' }}>
                廠商筆數：
                {groupCounts.map(g => `${g.name || g.code}(${g.count})`).join('、')}
              </div>
            )}
            <Table<ReportRow>
              columns={columns}
              dataSource={data}
              rowKey={(r, i) => `${r.FD130_15}-${r.FD130_12}-${r.unit_price}-${i}`}
              loading={loading}
              size="small"
              pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
              scroll={{ x: 700 }}
              style={{ marginBottom: 16 }}
            />
          </>
        )}
      </div>
    </PermissionGuard>
  );
};

export default D320Form;
